import { Injectable } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
    Clientele, Reseller,
    TravelAgent,
    TravelAgentByStaffMemberGQL,
    TravelAgentClienteleByBusinessIdGQL,
    TravelAgentResellerByBusinessIdGQL
} from '../../graphql'
import { UnsubscribeOnDestroy } from '../utility'
import { RoleManager } from "./role-manager";
import { PartnerUpdateTravelAgentListStaffGQL } from '../../graphql'
import { map } from 'rxjs/operators'

@Injectable({
    providedIn: 'root'
})
export class TravelAgentService extends UnsubscribeOnDestroy {
    travelAgentSubject = new ReplaySubject<TravelAgent | null | undefined>(1)
    clienteleSubject = new BehaviorSubject<Clientele | null | undefined>(undefined)
    resellerSubject = new BehaviorSubject<Reseller | null | undefined>(undefined)

    travelAgentId: string

    // Constructor
    constructor(private _roleManager: RoleManager,
                private _travelAgentGQL: TravelAgentByStaffMemberGQL,
                private _resellerGQL: TravelAgentResellerByBusinessIdGQL,
                private _clienteleGQL: TravelAgentClienteleByBusinessIdGQL,
                private _listStaffGQL: PartnerUpdateTravelAgentListStaffGQL,
                private _snackBar: MatSnackBar) {
        super()
        this.refreshTravelAgent()
    }

    refreshTravelAgent() {
        const refreshed$ = new Subject<void>()
        this._roleManager.queryAccountAsync(true)
            .pipe(
                takeUntil(refreshed$),
                takeUntil(this.destroy$),
            )
            .subscribe(account => {
                if (account && account.accountHolderId) {
                    this._travelAgentGQL
                        .fetch({ staffMemberId: account.accountHolderId }, { fetchPolicy: 'no-cache' })
                        .pipe(
                            takeUntil(this.destroy$),
                        )
                        .subscribe(result => {
                            if (result?.errors?.length) {
                                this._snackBar.open(`There was an error loading the travel agent, please refresh the page`)
                                console.error('There was an error loading the travel agent', result)
                                throw new Error(`There was an error loading the travel agent: ${result.errors[0].message}`)
                            }

                            if (!result.loading) {
                                let travelAgent = result.data['travelAgentByStaffMember']
                                if (!travelAgent) {
                                    this.travelAgentSubject.next(travelAgent)
                                    refreshed$.next()
                                    refreshed$.complete()
                                    return
                                }
                                this.travelAgentId = travelAgent.travelAgentId
                                this.travelAgentSubject.next(travelAgent)
                                refreshed$?.next()
                                refreshed$?.complete()

                                this.loadClientele(travelAgent.travelAgentId)
                                this.loadReseller(travelAgent.travelAgentId)
                            }
                        })
                }
            })
        return refreshed$
    }

    refreshClientele() {
        this.loadClientele(this.travelAgentId)
    }

    private loadClientele(travelAgentId: string) {
        this._clienteleGQL
            .watch({ businessId: travelAgentId }, { fetchPolicy: 'no-cache' })
            .valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                if (result?.errors?.length) {
                    this._snackBar.open(`There was an error loading the travel agent, please refresh the page`)
                    console.error('There was an error loading the travel agent', result)
                    throw new Error(`There was an error loading the travel agent: ${result.errors[0].message}`)
                }

                if (!result.loading) {
                    let clientele = result.data['clienteleByBusinessId'];
                    if (!clientele) {
                        throw 'Could not fetch token type'
                    }
                    this.clienteleSubject.next(clientele as Clientele)
                }
            })

    }

    private loadReseller(travelAgentId: string) {
        this._resellerGQL
            .watch({ businessId: travelAgentId }, { fetchPolicy: 'no-cache' })
            .valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                if (result?.errors?.length) {
                    const message = `There was an error loading the reseller: ${result.errors[0].message}`
                    this._snackBar.open(message)
                    throw new Error(message)
                }

                if (!result.loading) {
                    let reseller = result.data['resellerByBusinessId'];
                    if (!reseller) {
                        throw 'Could not fetch token type'
                    }
                    console.log('Travel agent ' + travelAgentId + ' loads reseller information: ', reseller)
                    this.resellerSubject.next(reseller as Reseller)
                }
            })
    }

    retrieveTravelAgentStaff(travelAgentId: string) {
        return this._listStaffGQL
            .fetch({ travelAgentId: travelAgentId}, { fetchPolicy: 'no-cache' })
            .pipe(
                takeUntil(this.destroy$),
                map((response) => {
                     if (response?.errors?.length) {
                        const message = 'There was an error retrieving travel agent staff.'
                        throw new Error(message)
                    }

                    return response
                })
            )
    }
}
